import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2Icon } from "lucide-react";
import * as React from "react";

import { cn } from "src/utilities/shadcnUtils";
import Skeleton from "../Skeleton";

const buttonVariants = cva(
  "cursor-pointer flex flex-col inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90",
        destructive:
          "bg-destructive-background text-destructive-foreground shadow-xs hover:bg-destructive-background/90",
        outline: "border border-input bg-transparent shadow-xs hover:bg-accent hover:text-accent-foreground",
        "outline-solid":
          "border border-input bg-transparent shadow-xs hover:bg-accent hover:text-accent-foreground",
        "outline:red":
          "border border-red-100 bg-transparent shadow-xs hover:bg-red-100 hover:text-red-600 text-red-500 hover:border-red-200",
        "outline:green":
          "border border-green-100 bg-transparent shadow-xs hover:bg-green-100 hover:text-green-600 text-green-500 hover:border-green-200",
        secondary: "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        "ghost:green": "hover:bg-green-100 hover:text-green/90 text-green",
        "ghost:red": "hover:bg-red-100 hover:text-red/70 text-red",
        link: "text-primary underline-offset-4 hover:underline",
        green: "bg-green text-white shadow-sm hover:bg-green/90",
        red: "bg-red text-white shadow-sm hover:bg-red/90",
        warning: "bg-yellow text-white shadow-sm hover:bg-yellow/90",
        blue: "bg-blue text-white shadow-sm hover:bg-blue/90",
      },
      size: {
        default: "h-9 px-4 py-2",
        "2xs": "h-5 rounded-sm px-1.5 text-[10px]",
        xxs: "h-6 rounded-sm px-1.5 text-[10px]",
        xs: "h-7 rounded-md px-2 text-[11px]",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const iconVariants = cva("", {
  variants: {
    size: {
      default: "h-4 w-4 mx-2",
      sm: "h-3.5 w-3.5 mx-1",
      lg: "h-4 w-4 mx-2",
      icon: "h-4 w-4 mr-0 ml-0",
      xs: "h-3.5 w-3.5 mx-1",
      xxs: "h-3 w-3 mx-1",
      "2xs": "h-2.5 w-2.5 mx-1",
    },
    placement: {
      start: "ml-0",
      end: "mr-0",
      only: "mr-0 ml-0",
      none: "mr-0",
    },
  },
  defaultVariants: {
    size: "default",
    placement: "end",
  },
});

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  pending?: boolean;
  pendingWithIcon?: boolean;
  iconPlacement?: "start" | "end" | "only" | "none";
}

// Lucide icons share a forwardRef render factory. Compare its body without invoking
// components or importing the entire icon registry; displayName has no Lucide prefix.
const lucideRenderSource = "render" in Loader2Icon ? String(Loader2Icon.render) : undefined;

function isLucideIcon(child: React.ReactNode) {
  if (!React.isValidElement(child)) return false;
  const type: unknown = child.type;
  return (
    typeof type === "object" &&
    type !== null &&
    "render" in type &&
    typeof type.render === "function" &&
    String(type.render) === lucideRenderSource
  );
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      loading,
      pending,
      pendingWithIcon,
      variant,
      size,
      asChild = false,
      children,
      iconPlacement,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    const slotChild = asChild ? React.Children.only(children) : undefined;
    const slotElement = React.isValidElement<{ children?: React.ReactNode }>(slotChild)
      ? slotChild
      : undefined;
    const contentChildren = slotElement ? slotElement.props.children : children;
    const childrenArray = React.Children.toArray(contentChildren);
    iconPlacement ??= isLucideIcon(childrenArray[0])
      ? childrenArray.length === 1
        ? "only"
        : "start"
      : isLucideIcon(childrenArray.at(-1))
        ? "end"
        : "none";
    const childrenWithoutIconChild =
      iconPlacement === "none"
        ? contentChildren
        : iconPlacement === "end"
          ? childrenArray.slice(0, -1)
          : iconPlacement === "start"
            ? childrenArray.slice(1)
            : null;
    const iconChild =
      iconPlacement === "none"
        ? null
        : iconPlacement === "end"
          ? childrenArray.slice(-1)[0]
          : childrenArray.slice(0, 1)[0];
    pendingWithIcon ??= Boolean(pending && React.isValidElement(iconChild));
    const pendingWithoutIcon = pending && !pendingWithIcon;

    const content = (
      <>
        {pendingWithoutIcon ? (
          <div className="min-h-[24px] flex items-center justify-center">
            <Loader2Icon className={cn("animate-spin", iconVariants({ size, placement: iconPlacement }))} />
          </div>
        ) : loading ? (
          <Skeleton className="h-4 w-20" />
        ) : null}
        {/* Keep the content there but hidden so that the width does not change on loading (note this needs the container to have a flex-col) */}
        <div
          className={cn(
            loading || pendingWithoutIcon
              ? "overflow-hidden h-0 flex flex-row items-center"
              : "cursor-pointer flex flex-row items-center",
            iconPlacement === "only" ? "justify-center" : "justify-between"
          )}
        >
          {iconPlacement === "start" || iconPlacement === "only" ? (
            pendingWithIcon ? (
              <Loader2Icon className={cn("animate-spin", iconVariants({ size, placement: iconPlacement }))} />
            ) : React.isValidElement<{ className?: string }>(iconChild) ? (
              React.cloneElement(iconChild, {
                className: cn(iconVariants({ size, placement: iconPlacement }), iconChild.props.className),
              })
            ) : (
              iconChild
            )
          ) : null}
          {childrenWithoutIconChild}
          {iconPlacement === "end" || iconPlacement === "none" ? (
            pendingWithIcon ? (
              <Loader2Icon className={cn("animate-spin", iconVariants({ size, placement: iconPlacement }))} />
            ) : iconPlacement === "end" && React.isValidElement<{ className?: string }>(iconChild) ? (
              React.cloneElement(iconChild, {
                className: cn(iconVariants({ size, placement: iconPlacement }), iconChild.props.className),
              })
            ) : (
              iconChild
            )
          ) : null}
        </div>
      </>
    );

    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
        {slotElement ? React.cloneElement(slotElement, undefined, content) : content}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
